import { PhoneNumberUtil } from 'google-libphonenumber';
export const checkForSameLocation = (lat1, lng1, lat2, lng2) => {
  const earthRadius = 3958.75; // in miles, change to 6371 for kilometer output

  function toRad(Value) {
    /** Converts numeric degrees to radians */
    return Value * Math.PI / 180;
  }

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const sindLat = Math.sin(dLat / 2);
  const sindLng = Math.sin(dLng / 2);

  const a =
    Math.pow(sindLat, 2) +
    Math.pow(sindLng, 2) *
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2));

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const dist = earthRadius * c;

  return dist < 0.5; // output distance, in MILES
};

export const formatHttpsLink = (link = '') => {

  if (link.includes('https://')) {
    return link
  }
  return `https://${link}`
}

const phoneUtil = PhoneNumberUtil.getInstance();

export const isPhoneValid = (phone) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};

// Debug function to help troubleshoot validation issues
export const debugPhoneValidation = (phone, country) => {
  console.log('=== Phone Validation Debug ===');
  console.log('Input phone:', phone);
  console.log('Input country:', country);

  if (!phone || !country) {
    console.log('Missing phone or country data');
    return;
  }

  try {
    // Clean the phone number
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    console.log('Cleaned phone:', cleanPhone);

    // Ensure the phone number has the country code
    let fullPhone = cleanPhone;
    if (!fullPhone.startsWith('+')) {
      fullPhone = `+${country.dialCode}${fullPhone}`;
    }
    console.log('Full phone with country code:', fullPhone);
    console.log('Country ISO2:', country.iso2?.toUpperCase());

    // Parse the phone number
    const phoneNumber = phoneUtil.parse(fullPhone, country.iso2?.toUpperCase());
    console.log('Parsed phone number:', phoneNumber);

    // Check if valid
    const isValid = phoneUtil.isValidNumber(phoneNumber);
    console.log('Is valid:', isValid);

    // Get number type
    const numberType = phoneUtil.getNumberType(phoneNumber);
    console.log('Number type:', numberType);

    // Get formatted number
    const formatted = phoneUtil.format(phoneNumber, phoneUtil.PhoneNumberFormat.E164);
    console.log('Formatted number:', formatted);

    return {
      isValid,
      numberType,
      formatted,
      phoneNumber
    };
  } catch (error) {
    console.log('Error during validation:', error);
    return null;
  }
};

// Enhanced phone validation for react-international-phone
export const validatePhoneWithCountry = (phone, country) => {
  try {
    if (!phone || !country) {
      return {
        isValid: false,
        error: "Phone number and country are required"
      };
    }

    // Clean the phone number - remove spaces and special characters
    const cleanPhone = phone.replace(/[^\d+]/g, '');

    // Ensure the phone number has the country code
    let fullPhone = cleanPhone;
    if (!fullPhone.startsWith('+')) {
      fullPhone = `+${country.dialCode}${fullPhone}`;
    }

    // Parse the phone number with the country code
    const phoneNumber = phoneUtil.parse(fullPhone, country.iso2.toUpperCase());
    const isValid = phoneUtil.isValidNumber(phoneNumber);

    if (!isValid) {
      return {
        isValid: false,
        error: `Please enter a valid phone number for ${country.name}`
      };
    }

    // Check if the number is of the correct type (mobile, fixed-line, etc.)
    const numberType = phoneUtil.getNumberType(phoneNumber);
    const validTypes = [
      phoneUtil.PhoneNumberType.MOBILE,
      phoneUtil.PhoneNumberType.FIXED_LINE,
      phoneUtil.PhoneNumberType.FIXED_LINE_OR_MOBILE
    ];

    if (!validTypes.includes(numberType)) {
      return {
        isValid: false,
        error: `Please enter a valid mobile or landline number for ${country.name}`
      };
    }

    return {
      isValid: true,
      error: null,
      formattedNumber: phoneUtil.format(phoneNumber, phoneUtil.PhoneNumberFormat.E164)
    };
  } catch (error) {
    console.log('Phone validation error:', error);
    return {
      isValid: false,
      error: "Invalid phone number format"
    };
  }
};

// Validate phone number length for specific country
export const validatePhoneLength = (phone, country) => {
  try {
    if (!phone || !country) {
      return {
        isValid: false,
        error: "Phone number and country are required"
      };
    }

    // Clean the phone number
    const cleanPhone = phone.replace(/[^\d+]/g, '');

    // Ensure the phone number has the country code
    let fullPhone = cleanPhone;
    if (!fullPhone.startsWith('+')) {
      fullPhone = `+${country.dialCode}${fullPhone}`;
    }

    // Parse the phone number
    const phoneNumber = phoneUtil.parse(fullPhone, country.iso2.toUpperCase());

    // Get the national number (without country code)
    const nationalNumber = phoneNumber.getNationalNumber();

    // Get metadata for the country
    const metadata = phoneUtil.getMetadataForRegion(country.iso2.toUpperCase());

    if (!metadata) {
      return {
        isValid: true, // If we can't get metadata, assume it's valid
        error: null
      };
    }

    // Get length constraints from metadata
    const generalDesc = metadata.getGeneralDesc();
    if (!generalDesc) {
      return {
        isValid: true,
        error: null
      };
    }

    const nationalNumberPattern = generalDesc.getNationalNumberPattern();
    if (!nationalNumberPattern) {
      return {
        isValid: true,
        error: null
      };
    }

    const minLength = nationalNumberPattern.getMinLength();
    const maxLength = nationalNumberPattern.getMaxLength();

    if (minLength && nationalNumber.length < minLength) {
      return {
        isValid: false,
        error: `Phone number is too short for ${country.name || country.iso2 || 'this country'}`
      };
    }

    if (maxLength && nationalNumber.length > maxLength) {
      return {
        isValid: false,
        error: `Phone number is too long for ${country.name || country.iso2 || 'this country'}`
      };
    }

    return {
      isValid: true,
      error: null
    };
  } catch (error) {
    console.log('Length validation error:', error);
    return {
      isValid: true, // If validation fails, assume it's valid
      error: null
    };
  }
};

// Comprehensive phone validation combining all checks
export const validatePhoneComprehensive = (phone, country) => {
  try {
    if (!phone || !country) {
      return {
        isValid: false,
        error: "Phone number and country are required"
      };
    }

    // Debug: Log the country object to see its structure
    console.log('Country object:', country);
    console.log('Country name:', country.name);
    console.log('Country iso2:', country.iso2);
    console.log('Country dialCode:', country.dialCode);

    // Clean the phone number - remove all non-digits except +
    const cleanPhone = phone.replace(/[^\d+]/g, '');

    // Ensure the phone number has the country code
    let fullPhone = cleanPhone;
    if (!fullPhone.startsWith('+')) {
      fullPhone = +`${country.dialCode}${cleanPhone}`;
    }
    
    // Special handling for Jamaica and other NANP countries with 3-digit area codes
    const countryCode = country.iso2?.toUpperCase();
    const isJamaica = countryCode === 'JM';
    const isNANPCountry = ['US', 'CA', 'JM'].includes(countryCode);
    
    // For Jamaica, extract the actual phone number part correctly
    let nationalNumber = '';
    if (isJamaica) {
      // Remove +1 and area code 876 to get the actual phone number
      const phoneWithoutCountryCode = fullPhone.replace('+1', '');
      if (phoneWithoutCountryCode.startsWith('876')) {
        nationalNumber = phoneWithoutCountryCode.substring(3); // Remove 876 area code
      } else {
        nationalNumber = phoneWithoutCountryCode;
      }
    } else {
      // For other countries, use the standard approach
      const phoneWithoutPlus = fullPhone.substring(1);
      const dialCodeLength = country.dialCode.length;
      nationalNumber = phoneWithoutPlus.substring(dialCodeLength);
    }

    console.log('National number extracted:', nationalNumber);
    console.log('Full phone for validation:', fullPhone);

    // Country-specific length validation for national number (without country code and area code)
    let isValidLength = true;
    let lengthError = '';
    
    switch (countryCode) {
      case 'JM': // Jamaica: 7 digits after area code 876
        if (nationalNumber.length !== 7) {
          isValidLength = false;
          lengthError = 'Jamaican phone numbers must be exactly 7 digits after the area code';
        }
        break;
      case 'US': // United States: 10 digits after country code 1
        if (nationalNumber.length !== 10) {
          isValidLength = false;
          lengthError = 'US phone numbers must be exactly 10 digits';
        }
        break;
      case 'CA': // Canada: 10 digits after country code 1
        if (nationalNumber.length !== 10) {
          isValidLength = false;
          lengthError = 'Canadian phone numbers must be exactly 10 digits';
        }
        break;
      case 'IN': // India: 10 digits after country code 91
        if (nationalNumber.length !== 10) {
          isValidLength = false;
          lengthError = 'Indian phone numbers must be exactly 10 digits';
        }
        break;
      case 'GB': // United Kingdom: 10-11 digits after country code 44
        if (nationalNumber.length < 10 || nationalNumber.length > 11) {
          isValidLength = false;
          lengthError = 'UK phone numbers must be 10-11 digits';
        }
        break;
      case 'AU': // Australia: 9 digits after country code 61
        if (nationalNumber.length !== 9) {
          isValidLength = false;
          lengthError = 'Australian phone numbers must be exactly 9 digits';
        }
        break;
      case 'DE': // Germany: 10-12 digits after country code 49
        if (nationalNumber.length < 10 || nationalNumber.length > 12) {
          isValidLength = false;
          lengthError = 'German phone numbers must be 10-12 digits';
        }
        break;
      case 'FR': // France: 9-10 digits after country code 33
        if (nationalNumber.length < 9 || nationalNumber.length > 10) {
          isValidLength = false;
          lengthError = 'French phone numbers must be 9-10 digits';
        }
        break;
      default:
        // For other countries, use basic validation
        if (nationalNumber.length < 7 || nationalNumber.length > 12) {
          isValidLength = false;
          lengthError = 'Phone number length is invalid for this country';
        }
    }
    
    if (!isValidLength) {
      const countryName = country.name || country.iso2 || 'this country';
      return {
        isValid: false,
        error: "Please enter a valid phone number"
      };
    }

    // For Jamaica, skip google-libphonenumber validation and rely on our custom validation
    if (isJamaica) {
      console.log('Jamaica number detected, using custom validation only');
      return {
        isValid: true,
        error: null,
        formattedNumber: +1876`${nationalNumber}`
      };
    }

    // Try google-libphonenumber validation for non-Jamaica countries
    try {
      const phoneNumber = phoneUtil.parse(fullPhone, country.iso2?.toUpperCase() || 'US');
      const isValid = phoneUtil.isValidNumber(phoneNumber);
      
      console.log('Phone validation result:', { 
        isValid, 
        fullPhone, 
        countryCode: country.iso2,
        phoneNumber: phoneNumber.toString(),
        numberType: phoneUtil.getNumberType(phoneNumber)
      });

      if (!isValid) {
        const countryName = country.name || country.iso2 || 'this country';
        console.log('Phone number is invalid for', countryName);
        return {
          isValid: false,
          error: `Please enter a valid phone number`
        };
      }

      // Check if the number is of the correct type (mobile, fixed-line, etc.)
      const numberType = phoneUtil.getNumberType(phoneNumber);
      const validTypes = [
        phoneUtil.PhoneNumberType.MOBILE,
        phoneUtil.PhoneNumberType.FIXED_LINE,
        phoneUtil.PhoneNumberType.FIXED_LINE_OR_MOBILE
      ];

      console.log('Number type:', numberType, 'Valid types:', validTypes);

      if (!validTypes.includes(numberType)) {
        const countryName = country.name || country.iso2 || 'this country';
        console.log('Number type is invalid for', countryName);
        return {
          isValid: false,
          error: `Please enter a valid mobile or landline number`
        };
      }

      return {
        isValid: true,
        error: null,
        formattedNumber: phoneUtil.format(phoneNumber, phoneUtil.PhoneNumberFormat.E164)
      };
    } catch (libPhoneError) {
      console.log('LibPhoneNumber validation failed, using fallback:', libPhoneError);
      // If libphonenumber fails, our length validation already passed, so return valid
      return {
        isValid: true,
        error: null
      };
    }

  } catch (error) {
    console.log('Comprehensive validation error:', error);
    
    // Enhanced fallback validation with Jamaica support
    const cleanPhone = phone.replace(/[^\d+]/g, '');
    const countryCode = country.iso2?.toUpperCase();
    
    // Extract national number for fallback validation
    let nationalNumber = '';
    if (countryCode === 'JM') {
      // Jamaica fallback: look for pattern +1876XXXXXXX or just the 7 digits
      const phoneDigitsOnly = cleanPhone.replace(/\D/g, '');
      if (phoneDigitsOnly.startsWith('1876')) {
        nationalNumber = phoneDigitsOnly.substring(4); // Remove 1876
      } else if (phoneDigitsOnly.startsWith('876')) {
        nationalNumber = phoneDigitsOnly.substring(3); // Remove 876
      } else {
        nationalNumber = phoneDigitsOnly;
      }
    } else {
      // Standard fallback for other countries
      const phoneWithoutPlus = cleanPhone.startsWith('+') ? cleanPhone.substring(1) : cleanPhone;
      const dialCodeLength = country.dialCode ? country.dialCode.length : 0;
      nationalNumber = phoneWithoutPlus.substring(dialCodeLength);
    }
    
    console.log('Fallback validation - National number:', nationalNumber);
    
    // Country-specific fallback validation
    let minLength = 7;
    let maxLength = 12;
    let errorMessage = '';
    
    switch (countryCode) {
      case 'JM': // Jamaica: 7 digits
        minLength = 7;
        maxLength = 7;
        errorMessage = 'Jamaican phone numbers must be exactly 7 digits';
        break;
      case 'US': // United States: 10 digits
        minLength = 10;
        maxLength = 10;
        errorMessage = 'US phone numbers must be exactly 10 digits';
        break;
      case 'CA': // Canada: 10 digits
        minLength = 10;
        maxLength = 10;
        errorMessage = 'Canadian phone numbers must be exactly 10 digits';
        break;
      case 'IN': // India: 10 digits
        minLength = 10;
        maxLength = 10;
        errorMessage = 'Indian phone numbers must be exactly 10 digits';
        break;
      case 'GB': // United Kingdom: 10-11 digits
        minLength = 10;
        maxLength = 11;
        errorMessage = 'UK phone numbers must be 10-11 digits';
        break;
      case 'AU': // Australia: 9 digits
        minLength = 9;
        maxLength = 9;
        errorMessage = 'Australian phone numbers must be exactly 9 digits';
        break;
      case 'DE': // Germany: 10-12 digits
        minLength = 10;
        maxLength = 12;
        errorMessage = 'German phone numbers must be 10-12 digits';
        break;
      case 'FR': // France: 9-10 digits
        minLength = 9;
        maxLength = 10;
        errorMessage = 'French phone numbers must be 9-10 digits';
        break;
      default:
        errorMessage = 'Phone number length is invalid for this country';
    }
    
    if (nationalNumber.length < minLength) {
      return {
        isValid: false,
        error: "Please enter a valid phone number"
      };
    }
    
    if (nationalNumber.length > maxLength) {
      return {
        isValid: false,
        error: "Please enter a valid phone number"
      };
    }

    return {
      isValid: true,
      error: null
    };
  }
};